import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { WhiteLabelProvider } from "@/contexts/WhiteLabelContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import HowItWorks from "@/pages/HowItWorks";
import Auth from "@/pages/Auth";
import UpgradeSignup from "@/pages/UpgradeSignup";
import Dashboard from "@/pages/Dashboard";
import RecipeSearch from "@/pages/RecipeSearch";
import RecipeDetail from "@/pages/RecipeDetail";
import MealPlanner from "@/pages/MealPlanner";
import GroceryLists from "@/pages/GroceryLists";
import GroceryListDetail from "@/pages/GroceryListDetail";
import Favorites from "@/pages/Favorites";
import Pantry from "@/pages/Pantry";
import Subscription from "@/pages/Subscription";
import SharedMealPlan from "@/pages/SharedMealPlan";
import JoinHousehold from "@/pages/JoinHousehold";
import AdminAnalytics from "@/pages/AdminAnalytics";
import WhiteLabelAdmin from "@/pages/WhiteLabelAdmin";
import DemoLanding from "@/pages/DemoLanding";
import EnterprisePage from "@/pages/EnterprisePage";
import IntegrationStory from "@/pages/IntegrationStory";
import HarrisTeeterDemo from "@/pages/demos/HarrisTeeterDemo";
import HarrisTeeterLeaveBehind from "@/pages/demos/HarrisTeeterLeaveBehind";

// Components
import Navbar from "@/components/Navbar";
import MealConcierge from "@/components/MealConcierge";

// Protected Route Component
const ProtectedRoute = ({ children, allowDemo = false }) => {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get('demo') === 'true';
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Allow demo mode access without authentication
  if (allowDemo && isDemoMode) {
    return children;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Redirect with preserved query params - for upgrade flow
const RedirectWithParams = ({ to }) => {
  const location = useLocation();
  const targetUrl = location.search ? `${to}${location.search}` : to;
  return <Navigate to={targetUrl} replace />;
};

// Layout with Navbar
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>{children}</main>
      <MealConcierge />
    </div>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/integrations" element={<IntegrationStory />} />
      <Route path="/demo/harris-teeter" element={<HarrisTeeterDemo />} />
      <Route path="/demo/harris-teeter/overview" element={<HarrisTeeterLeaveBehind />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route 
        path="/auth" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Auth />} 
      />
      <Route 
        path="/upgrade" 
        element={isAuthenticated ? <RedirectWithParams to="/subscription" /> : <UpgradeSignup />} 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowDemo={true}>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute allowDemo={true}>
            <AppLayout>
              <RecipeSearch />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RecipeDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-planner"
        element={
          <ProtectedRoute allowDemo={true}>
            <AppLayout>
              <MealPlanner />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grocery-lists"
        element={
          <ProtectedRoute allowDemo={true}>
            <AppLayout>
              <GroceryLists />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/grocery-lists/:id"
        element={
          <ProtectedRoute allowDemo={true}>
            <AppLayout>
              <GroceryListDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Favorites />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pantry"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Pantry />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Subscription />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Subscription />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/cancel"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Subscription />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Admin Analytics Route */}
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AdminAnalytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* White-Label Admin Route */}
      <Route
        path="/admin/whitelabel"
        element={
          <ProtectedRoute>
            <AppLayout>
              <WhiteLabelAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Public shared meal plan route - no auth required */}
      <Route path="/shared/:shareId" element={<SharedMealPlan />} />
      {/* Household invite route - handles both auth and unauth users */}
      <Route path="/join-household/:inviteId" element={<JoinHousehold />} />
    </Routes>
  );
}

function App() {
  // Extract tenant from URL params (for white-label deployments)
  const urlParams = new URLSearchParams(window.location.search);
  const tenantId = urlParams.get('tenant') || 'default';

  return (
    <BrowserRouter>
      <WhiteLabelProvider tenantId={tenantId}>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </WhiteLabelProvider>
    </BrowserRouter>
  );
}

export default App;
